import { ref, uploadBytesResumable, type UploadTaskSnapshot } from 'firebase/storage';
import { storage } from './firebase';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percent: number;
  state: 'running' | 'paused' | 'success' | 'error' | 'canceled';
}

/**
 * Upload the audit zip directly to Storage. The path triggers the Cloud
 * Function `onAuditUploaded` which parses & writes Firestore docs.
 */
export function uploadAuditZip(
  orgId: string,
  auditId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  uploaderUid?: string,
): { promise: Promise<string>; cancel: () => void } {
  const path = `orgs/${orgId}/audits/${auditId}/upload.zip`;
  const r = ref(storage, path);
  const task = uploadBytesResumable(r, file, {
    contentType: 'application/zip',
    customMetadata: {
      orgId,
      auditId,
      originalName: file.name,
      uploadedBy: uploaderUid ?? '',
    },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(path),
    );
  });

  return { promise, cancel: () => task.cancel() };
}

/**
 * Reduce a user-supplied file name to one safe Storage path segment
 * (same constraints as functions/lib/documents.py `validate_file_name`).
 */
export function safeStorageFileName(name: string): string {
  const cleaned = name
    .replace(/[\p{C}]/gu, '')
    .replace(/[/\\]/g, '_')
    .trim()
    .slice(0, 200);
  return cleaned && cleaned !== '.' && cleaned !== '..' ? cleaned : 'document';
}

/**
 * Upload a generic organisation document (scope, report PDF, etc.) to Storage.
 * Path must match `orgs/{orgId}/documents/{docId}/{filename}` — the Cloud
 * Function `createOrgDocument` allocates the docId and path before this upload,
 * and `finalizeOrgDocument` must be called afterwards to record `filePath`.
 */
export function uploadOrgDocument(
  orgId: string,
  docId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  uploaderUid?: string,
): { promise: Promise<string>; fileName: string; cancel: () => void } {
  const fileName = safeStorageFileName(file.name);
  const path = `orgs/${orgId}/documents/${docId}/${fileName}`;
  const r = ref(storage, path);
  const task = uploadBytesResumable(r, file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      orgId,
      docId,
      originalName: file.name,
      uploadedBy: uploaderUid ?? '',
    },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(path),
    );
  });

  return { promise, fileName, cancel: () => task.cancel() };
}

/**
 * Upload a single image attachment for a support ticket message.
 * The `storagePath` is pre-allocated by the createTicket / addTicketMessage Cloud Function.
 */
export function uploadTicketAttachment(
  storagePath: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): { promise: Promise<string>; cancel: () => void } {
  const r = ref(storage, storagePath);
  const task = uploadBytesResumable(r, file, {
    contentType: file.type,
    customMetadata: { originalName: file.name },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(storagePath),
    );
  });

  return { promise, cancel: () => task.cancel() };
}

/** Validate basic structure of an audit zip — must contain run_metadata.json. */
export async function validateAuditZip(file: File): Promise<{ ok: boolean; error?: string }> {
  if (!/\.zip$/i.test(file.name)) return { ok: false, error: 'Le fichier doit être un .zip.' };
  if (file.size > 500 * 1024 * 1024) return { ok: false, error: 'Le fichier dépasse 500 Mo.' };
  if (file.size < 1024) return { ok: false, error: 'Fichier trop petit pour être un output valide.' };
  return { ok: true };
}
